import React, { useCallback, useEffect, useState } from "react";
import {
  DataTable,
  TableAction,
  TableColumn,
} from "../../../components/DataTable";
import { Modal } from "../../../components/Modal";
import { Notification } from "../../../components/Notification";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { modoHistoriaOptions } from "../../../config/entityColors";
import {
  EtapaHistoria,
  GestionModoHistoriaService,
  MundoHistoria,
} from "../../../services/admin/gestion-modo-historia/gestion-modo-historia.service";
import { AdminLayout } from "../components";
import { EntitySelector } from "../components/EntitySelector";
interface NotificationState {
  isOpen: boolean;
  type: "success" | "error" | "info" | "warning";
  title: string;
  message: string;
}

type EntityType = "mundo" | "etapa";

interface FormData {
  nombre: string;
  descripcion?: string;
  icono?: string;
  color?: string;
  mundoHistoriaId?: number;
  orden?: number;
}

export const GestionModoHistoriaPage: React.FC = () => {
  const [selectedEntity, setSelectedEntity] = useState<EntityType>("mundo");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    nombre: "",
    descripcion: "",
    icono: "",
    color: "",
    mundoHistoriaId: undefined,
    orden: 1,
  });

  // Estados para cada entidad
  const [mundos, setMundos] = useState<MundoHistoria[]>([]);
  const [etapas, setEtapas] = useState<EtapaHistoria[]>([]);
  const [loading, setLoading] = useState(false);

  // Estado para notificaciones
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
  });

  // Estados para confirmación de eliminación
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  // Estados para carga masiva
  const [isMasivaModalOpen, setIsMasivaModalOpen] = useState(false);
  const [masivaFormData, setMasivaFormData] = useState({
    mundoId: "",
    cantidad: "",
  });

  // Funciones para manejar notificaciones
  const showNotification = useCallback(
    (
      type: "success" | "error" | "info" | "warning",
      title: string,
      message: string
    ) => {
      setNotification({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    []
  );

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, isOpen: false }));
  };

  // Función para mostrar confirmación de eliminación
  const confirmDelete = (id: number) => {
    setItemToDelete(id);
    setShowDeleteConfirm(true);
  };

  // Función para cancelar eliminación
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // Cargar datos según la entidad seleccionada
  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      switch (selectedEntity) {
        case "mundo":
          const mundosData = await GestionModoHistoriaService.getMundos();
          setMundos(mundosData || []);
          break;
        case "etapa":
          const etapasData = await GestionModoHistoriaService.getEtapas();
          setEtapas(etapasData || []);
          break;
      }
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.response?.data?.message || "Error al cargar los datos"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, showNotification]);

  // Cargar datos al cambiar de entidad
  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const resetForm = () => {
    setFormData({
      nombre: "",
      descripcion: "",
      icono: "",
      color: "",
      mundoHistoriaId: undefined,
      orden: 1,
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedEntity === "mundo") {
        if (isEditing && editingId) {
          await GestionModoHistoriaService.updateMundo(editingId, {
            nombre: formData.nombre,
            descripcion: formData.descripcion,
            icono: formData.icono,
            color: formData.color,
            orden: formData.orden,
          });
          showNotification(
            "success",
            "Éxito",
            "Mundo actualizado correctamente"
          );
        } else {
          await GestionModoHistoriaService.createMundo({
            nombre: formData.nombre,
            descripcion: formData.descripcion,
            icono: formData.icono,
            color: formData.color,
            orden: formData.orden,
          });
          showNotification("success", "Éxito", "Mundo creado correctamente");
        }
      } else if (selectedEntity === "etapa") {
        if (!formData.mundoHistoriaId) {
          showNotification("error", "Error", "Debe seleccionar un mundo");
          return;
        }
        if (isEditing && editingId) {
          await GestionModoHistoriaService.updateEtapa(editingId, {
            nombre: formData.nombre,
            mundoId: formData.mundoHistoriaId,
            orden: formData.orden,
          });
          showNotification(
            "success",
            "Éxito",
            "Etapa actualizada correctamente"
          );
        } else {
          await GestionModoHistoriaService.createEtapa({
            nombre: formData.nombre,
            mundoId: formData.mundoHistoriaId,
            orden: formData.orden,
          });
          showNotification("success", "Éxito", "Etapa creada correctamente");
        }
      }
      closeModal();
      cargarDatos();
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.response?.data?.message || "Error al procesar la solicitud"
      );
    }
  };

  const handleEdit = (item: any) => {
    if (selectedEntity === "mundo") {
      setFormData({
        nombre: item.nombre,
        descripcion: item.descripcion || "",
        icono: item.icono || "",
        color: item.color || "",
        orden: item.orden || 1,
      });
    } else if (selectedEntity === "etapa") {
      setFormData({
        nombre: item.nombre,
        mundoHistoriaId: item.mundoId,
        orden: item.orden || 1,
      });
    }
    setIsEditing(true);
    setEditingId(item.idMundoHistoria || item.idEtapaHistoria);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      if (selectedEntity === "mundo") {
        await GestionModoHistoriaService.deleteMundo(id);
        showNotification("success", "Éxito", "Mundo eliminado correctamente");
      } else if (selectedEntity === "etapa") {
        await GestionModoHistoriaService.deleteEtapa(id);
        showNotification("success", "Éxito", "Etapa eliminada correctamente");
      }
      cargarDatos();
      cancelDelete();
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.response?.data?.message || "Error al eliminar"
      );
    }
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Funciones para carga masiva
  const openMasivaModal = () => {
    setIsMasivaModalOpen(true);
    setMasivaFormData({ mundoId: "", cantidad: "" });
  };

  const closeMasivaModal = () => {
    setIsMasivaModalOpen(false);
    setMasivaFormData({ mundoId: "", cantidad: "" });
  };

  const handleMasivaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const mundoId = parseInt(masivaFormData.mundoId);
      const cantidad = parseInt(masivaFormData.cantidad);

      if (!mundoId || !cantidad) {
        showNotification("error", "Error", "Debe completar todos los campos");
        return;
      }

      if (cantidad <= 0 || cantidad > 100) {
        showNotification(
          "error",
          "Error",
          "La cantidad debe estar entre 1 y 100"
        );
        return;
      }

      const resultado = await GestionModoHistoriaService.createEtapasMasivas(
        mundoId,
        cantidad
      );

      showNotification(
        "success",
        "Éxito",
        `${resultado.mensaje} en ${resultado.mundoNombre}`
      );

      closeMasivaModal();
      cargarDatos(); // Recargar datos para mostrar las nuevas etapas
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.response?.data?.message || "Error al crear las etapas masivas"
      );
    }
  };

  const getEntityTitle = () => {
    switch (selectedEntity) {
      case "mundo":
        return "Mundos de Historia";
      case "etapa":
        return "Etapas de Historia";
      default:
        return "";
    }
  };

  const getCurrentData = () => {
    switch (selectedEntity) {
      case "mundo":
        return mundos || [];
      case "etapa":
        return etapas || [];
      default:
        return [];
    }
  };

  const getEntityName = () => {
    switch (selectedEntity) {
      case "mundo":
        return "Mundo";
      case "etapa":
        return "Etapa";
      default:
        return "";
    }
  };

  // Configuración de columnas para la tabla
  const getColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      { key: "nombre", label: "Nombre" },
      { key: "actions", label: "Acciones", type: "actions" },
    ];

    if (selectedEntity === "mundo") {
      baseColumns.splice(1, 0, { key: "orden", label: "Orden" });
      baseColumns.splice(2, 0, { key: "descripcion", label: "Descripción" });
      baseColumns.splice(3, 0, { key: "icono", label: "Icono" });
      baseColumns.splice(4, 0, { key: "color", label: "Color" });
    } else if (selectedEntity === "etapa") {
      baseColumns.splice(1, 0, { key: "orden", label: "Orden" });
      baseColumns.splice(2, 0, { key: "mundo", label: "Mundo" });
    }

    return baseColumns;
  };

  // Configuración de acciones para la tabla
  const actions: TableAction[] = [
    {
      label: "Editar",
      onClick: handleEdit,
      variant: "edit",
    },
    {
      label: "Eliminar",
      onClick: (item) =>
        confirmDelete(item.idMundoHistoria || item.idEtapaHistoria),
      variant: "delete",
    },
  ];

  // Procesar datos para mostrar el nombre del mundo en las etapas
  const getProcessedData = () => {
    const data = getCurrentData();
    if (selectedEntity === "mundo") {
      const processedData = data.map((mundo: any) => ({
        ...mundo,
        orden: mundo.orden || 1,
      }));

      // Ordenar por el campo orden
      return processedData.sort((a: any, b: any) => {
        const ordenA = Number(a.orden) || 1;
        const ordenB = Number(b.orden) || 1;
        return ordenA - ordenB;
      });
    } else if (selectedEntity === "etapa") {
      const processedData = data.map((etapa: any) => {
        const mundo = mundos.find((m) => m.idMundoHistoria === etapa.mundoId);
        return {
          ...etapa,
          orden: etapa.orden || 1,
          mundo: mundo?.nombre || "-",
          mundoOrden: mundo?.orden || 1,
        };
      });

      // Ordenar primero por orden del mundo y luego por orden de la etapa
      return processedData.sort((a: any, b: any) => {
        const mundoOrdenA = Number(a.mundoOrden) || 1;
        const mundoOrdenB = Number(b.mundoOrden) || 1;

        // Si el orden del mundo es diferente, ordenar por eso
        if (mundoOrdenA !== mundoOrdenB) {
          return mundoOrdenA - mundoOrdenB;
        }

        // Si el orden del mundo es igual, ordenar por orden de la etapa
        const ordenA = Number(a.orden) || 1;
        const ordenB = Number(b.orden) || 1;
        return ordenA - ordenB;
      });
    }
    return data;
  };

  // Obtener opciones de filtro para mundos
  const getFilterOptions = () => {
    if (selectedEntity === "etapa") {
      return mundos.map((mundo) => ({
        value: mundo.nombre,
        label: mundo.nombre,
      }));
    }
    return [];
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-600">
              Gestión Modo Historia
            </h1>
            <p className="text-gray-600">
              Gestiona mundos y etapas del modo historia
            </p>
          </div>
          <div className="flex gap-3">
            {selectedEntity === "etapa" && (
              <button
                onClick={openMasivaModal}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
              >
                Carga Masiva
              </button>
            )}
            <button
              onClick={openCreateModal}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
            >
              Crear Nuevo {getEntityName()}
            </button>
          </div>
        </div>

        {/* Selector de entidad */}
        <EntitySelector
          selectedEntity={selectedEntity}
          onEntityChange={(entityId) =>
            setSelectedEntity(entityId as EntityType)
          }
          options={modoHistoriaOptions}
        />

        {/* Tabla de registros */}
        <DataTable
          title={getEntityTitle()}
          data={getProcessedData()}
          columns={getColumns()}
          actions={actions}
          loading={loading}
          emptyMessage={`No hay ${getEntityTitle().toLowerCase()} registrados.`}
          pagination={{
            enabled: true,
            itemsPerPage: 10,
          }}
          filters={{
            enabled: selectedEntity === "etapa",
            filterKey: "mundo",
            filterLabel: "Mundo",
            filterType: "select",
            filterOptions: getFilterOptions(),
          }}
        />

        {/* Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={
            isEditing ? `Editar ${getEntityName()}` : `Crear ${getEntityName()}`
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                type="text"
                value={formData.nombre}
                onChange={(e) =>
                  setFormData({ ...formData, nombre: e.target.value })
                }
                required
                placeholder={`Nombre del ${getEntityName().toLowerCase()}`}
              />
            </div>

            {selectedEntity === "mundo" && (
              <>
                <div>
                  <Label htmlFor="orden">Orden *</Label>
                  <Input
                    id="orden"
                    type="number"
                    min="1"
                    value={formData.orden || 1}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orden: Number(e.target.value),
                      })
                    }
                    required
                    placeholder="Orden del mundo"
                  />
                </div>
                <div>
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    value={formData.descripcion}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        descripcion: e.target.value,
                      })
                    }
                    placeholder="Descripción del mundo (opcional)"
                  />
                </div>
                <div>
                  <Label htmlFor="icono">Icono</Label>
                  <Input
                    id="icono"
                    type="text"
                    value={formData.icono}
                    onChange={(e) =>
                      setFormData({ ...formData, icono: e.target.value })
                    }
                    placeholder="Ej: 0xf0134 (hex), fa-home, o ruta de imagen"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puede ser código hexadecimal (0xf0134), nombre de Font
                    Awesome (fa-home), o URL de imagen
                  </p>
                </div>
                <div>
                  <Label htmlFor="color">Color</Label>
                  <Input
                    id="color"
                    type="text"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    placeholder="Ej: #FF5733, blue, rgb(255, 87, 51)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Color en formato hexadecimal, nombre o RGB
                  </p>
                </div>
              </>
            )}

            {selectedEntity === "etapa" && (
              <>
                <div>
                  <Label htmlFor="mundoHistoriaId">Mundo *</Label>
                  <select
                    id="mundoHistoriaId"
                    value={formData.mundoHistoriaId || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        mundoHistoriaId: Number(e.target.value),
                      })
                    }
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="">Seleccionar mundo</option>
                    {mundos.map((mundo) => (
                      <option
                        key={mundo.idMundoHistoria}
                        value={mundo.idMundoHistoria}
                      >
                        {mundo.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="orden">Orden *</Label>
                  <Input
                    id="orden"
                    type="number"
                    min="1"
                    value={formData.orden || 1}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orden: Number(e.target.value),
                      })
                    }
                    required
                    placeholder="Orden de la etapa"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancelar
              </Button>
              <Button type="submit">
                {isEditing ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Notificación */}
        <Notification
          isOpen={notification.isOpen}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={closeNotification}
        />

        {/* Modal de Carga Masiva */}
        <Modal
          isOpen={isMasivaModalOpen}
          onClose={closeMasivaModal}
          title="Carga Masiva de Etapas"
        >
          <form onSubmit={handleMasivaSubmit} className="space-y-4">
            <div>
              <Label htmlFor="mundoId">Mundo *</Label>
              <select
                id="mundoId"
                value={masivaFormData.mundoId}
                onChange={(e) =>
                  setMasivaFormData({
                    ...masivaFormData,
                    mundoId: e.target.value,
                  })
                }
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Seleccionar mundo</option>
                {mundos.map((mundo) => (
                  <option
                    key={mundo.idMundoHistoria}
                    value={mundo.idMundoHistoria}
                  >
                    {mundo.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="cantidad">Cantidad de Etapas *</Label>
              <Input
                id="cantidad"
                type="number"
                min="1"
                max="100"
                value={masivaFormData.cantidad}
                onChange={(e) =>
                  setMasivaFormData({
                    ...masivaFormData,
                    cantidad: e.target.value,
                  })
                }
                required
                placeholder="Ej: 7"
              />
              <p className="text-sm text-gray-500 mt-1">
                Ingrese la cantidad de etapas a crear (1-100)
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeMasivaModal}
              >
                Cancelar
              </Button>
              <Button type="submit">Crear Etapas</Button>
            </div>
          </form>
        </Modal>

        {/* Confirmación de eliminación */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 border border-blue-100">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Confirmar eliminación
                    </h3>
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-sm text-gray-500">
                    ¿Estás seguro de que quieres eliminar este{" "}
                    {getEntityName().toLowerCase()}? Esta acción no se puede
                    deshacer.
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleDelete(itemToDelete!)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
                  >
                    Eliminar
                  </button>
                  <button
                    onClick={cancelDelete}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
